import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useEffectOnce } from '@chilecompra/react-kit'
import { Card, Skeleton, Table, TableCell, TableRow, Tag, Typography } from '@chilecompra/react-kit/components'
import PurchaseOrderStatusDeclaration from './PurchaseOrderStatus.declaration'
import { formatTypeCurrency } from '../../modules/utils/formatTypeCurrency'
import { onGetPurchaseOrderStatusThunk } from './PurchaseOrderStatus.actions'
import { Divider, Grid, Strong } from './PurchaseOrderStatus.styles'

/**
 * The PurchaseOrderStatusContainer's container.
 */
const PurchaseOrderStatusContainer = () => {
  const { basicInfo, loading, products, purchaseOrderCode, swornDeclaration } = useSelector(
    state => state.purchaseOrderStatus
  )
  const { basicInfo: basicInfoQuoteSummary } = useSelector(state => state.quoteBasicInformation)
  const { currency, id: requestQuoteId } = basicInfoQuoteSummary
  const dispatch = useDispatch()
  const taxValue = 19

  useEffectOnce(() => {
    if (purchaseOrderCode !== 0) {
      dispatch(onGetPurchaseOrderStatusThunk({ purchaseOrderId: purchaseOrderCode, requestQuoteId }))
    }
  })

  return (
    <>
      <Grid container xs={12} sm={12}>
        <Grid item xs={12} sm={5}>
          <Typography color="black1" fontSize="30px" fontWeight="bold" lineHeight="26px" variant="h3">
            Orden de Compra {basicInfo.purchaseOrderId}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={1}>
          <Tag text={basicInfo.textState} color="default" />
        </Grid>
        <Grid>
          <Typography color="black1" fontSize="22px" lineHeight="31px" variant="h2">
            Cotización para Compra Ágil {basicInfo.quoteId}
          </Typography>
          <Typography color="gray1" fontSize="13px" fontWeight="bold" lineHeight="16px" variant="caption">
            FECHA DE ENVIO {basicInfo.shippingDate}
          </Typography>
        </Grid>
      </Grid>
      <Grid container sm={12} margin="20px 0">
        <Grid container sm={8} margin="10px 20px 10px 0">
          <Grid container>
            <Grid item>
              <Divider />
            </Grid>
            <Grid item sm={2}>
              <Typography color="black1" fontSize="16px" fontWeight="bold" lineHeight="20px" variant="body2">
                Nombre de la cotización:
              </Typography>
            </Grid>
            <Grid item xs={12} sm>
              <Typography color="black1" fontSize="16px" lineHeight="18px" variant="body2">
                {loading ? <Skeleton /> : basicInfo.name}
              </Typography>
            </Grid>
          </Grid>
          <Grid item>
            <Divider />
          </Grid>
          <Grid container>
            <Grid item sm={2}>
              <Typography color="black1" fontSize="16px" fontWeight="bold" lineHeight="20px" variant="body2">
                Descripción de la cotización:
              </Typography>
            </Grid>
            <Grid item xs={12} sm>
              <Typography color="black1" fontSize="16px" lineHeight="18px" variant="body2">
                {loading ? <Skeleton /> : basicInfo.description}
              </Typography>
            </Grid>
          </Grid>
          <Grid item>
            <Divider />
          </Grid>
          <Grid container>
            <Grid item sm={2}>
              <Typography color="black1" fontSize="16px" fontWeight="bold" lineHeight="20px" variant="body2">
                Dirección de entrega:
              </Typography>
            </Grid>
            <Grid item xs={12} sm>
              <Typography color="black1" fontSize="16px" lineHeight="18px" variant="body2">
                {loading ? <Skeleton /> : basicInfo.address}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        <Grid container sm>
          <Card color="primary">
            <Grid container margin="20px 0">
              <Grid item xs={12} sm={5}>
                <Typography color="black1" fontSize="16px" fontWeight="bold" lineHeight="18px" variant="body2">
                  Organismo Público solicitante
                </Typography>
              </Grid>
              <Grid item sm>
                <Typography color="black1" fontSize="15px" lineHeight="19px" variant="body2">
                  {loading ? <Skeleton /> : basicInfo.organism}
                </Typography>
              </Grid>
            </Grid>
            <Grid container margin="20px 0">
              <Grid item xs={12} sm={5}>
                <Typography color="black1" fontSize="16px" fontWeight="bold" lineHeight="18px" variant="body2">
                  Fecha de publicación para cotizar
                </Typography>
              </Grid>
              <Grid item sm>
                <Typography color="black1" fontSize="16px" lineHeight="18px" variant="body2">
                  {loading ? <Skeleton /> : basicInfo.publicationDate}
                </Typography>
              </Grid>
            </Grid>
            <Grid container margin="20px 0">
              <Grid item xs={12} sm={5}>
                <Typography color="black1" fontSize="16px" fontWeight="bold" lineHeight="18px" variant="body2">
                  Fecha de cierre para cotizar
                </Typography>
              </Grid>
              <Grid item sm>
                <Typography color="black1" fontSize="16px" lineHeight="18px" variant="body2">
                  {loading ? <Skeleton /> : basicInfo.deadline}
                </Typography>
              </Grid>
            </Grid>
            <Grid container margin="20px 0">
              <Grid item xs={12} sm={5}>
                <Typography color="black1" fontSize="16px" fontWeight="bold" lineHeight="18px" variant="body2">
                  Plazo de entrega
                </Typography>
              </Grid>
              <Grid item sm>
                <Typography color="black1" fontSize="16px" lineHeight="18px" variant="body2">
                  {loading ? <Skeleton /> : basicInfo.deliveryTerm}
                </Typography>
              </Grid>
            </Grid>
          </Card>
        </Grid>
      </Grid>
      <Divider />
      <Typography color="black1" fontSize="26px" lineHeight="26px" margin="30px 0" variant="h3">
        Detalle de productos cotizados
      </Typography>
      <Typography color="black1" fontSize="16px" lineHeight="18px" variant="body2">
        {loading ? <Skeleton /> : basicInfo.productsDescription}
      </Typography>
      <Table accessibility={{ label: 'Detalle de productos cotizados' }} margin="20px 0">
        <TableRow enableHead>
          <TableCell>
            <Typography color="black3" fontSize="16px" fontWeight="bold" lineHeight="18px" variant="body2">
              ID
            </Typography>
          </TableCell>
          <TableCell>
            <Typography color="black3" fontSize="16px" fontWeight="bold" lineHeight="18px" variant="body2">
              PRODUCTO / SERVICIO
            </Typography>
          </TableCell>
          <TableCell>
            <Typography color="black3" fontSize="16px" fontWeight="bold" lineHeight="18px" variant="body2">
              CANTIDAD
            </Typography>
          </TableCell>
          <TableCell>
            <Typography color="black3" fontSize="16px" fontWeight="bold" lineHeight="18px" variant="body2">
              PRECIO UNITARIO
            </Typography>
          </TableCell>
          <TableCell>
            <Typography color="black3" fontSize="16px" fontWeight="bold" lineHeight="18px" variant="body2">
              SUBTOTAL
            </Typography>
          </TableCell>
        </TableRow>
        {products.map((product, i) => (
          <TableRow key={product.dni}>
            <TableCell width="8px">{i + 1}</TableCell>
            <TableCell>
              <Typography color="black1" fontSize="16px" lineHeight="18px" variant="body2">
                {loading ? <Skeleton /> : product.name}
              </Typography>
              <Typography color="gray1" fontSize="16px" lineHeight="18px" variant="body2">
                {loading ? <Skeleton /> : product.description}
              </Typography>
            </TableCell>
            <TableCell>
              {loading ? <Skeleton /> : product.quantity}
              Unidad
            </TableCell>
            <TableCell>{product.unitPrice ? formatTypeCurrency(currency, product.unitPrice) : ''}</TableCell>
            <TableCell>{product.amount ? formatTypeCurrency(currency, product.amount) : ''}</TableCell>
          </TableRow>
        ))}
      </Table>
      <Grid container direction="row" justifyContent="flex-end">
        <Grid container sm={3} margin="20px 0 40px">
          <Card color="primary" enableBorder>
            <Grid container>
              <Grid item sm={6}>
                <Typography color="black2" fontSize="14px" lineHeight="18px" variant="body2">
                  Valor neto
                </Typography>
              </Grid>
              <Grid item sm>
                <Typography color="black2" fontSize="14px" lineHeight="18px" variant="body2">
                  {basicInfo.netAmount ? formatTypeCurrency(currency, basicInfo.netAmount) : ''}
                </Typography>
              </Grid>
            </Grid>
            <Grid container>
              <Grid item sm={6}>
                <Typography color="black2" fontSize="14px" lineHeight="18px" variant="body2">
                  {basicInfo.nameTax} {`${taxValue} %`}
                </Typography>
              </Grid>
              <Grid item sm>
                <Typography color="black2" fontSize="14px" lineHeight="18px" variant="body2">
                  {basicInfo.nameTax ? formatTypeCurrency(currency, basicInfo.tax) : ''}
                </Typography>
              </Grid>
            </Grid>
            <Grid container>
              <Grid item sm={6}>
                <Typography color="black2" fontSize="14px" fontWeight="bold" lineHeight="18px" variant="body2">
                  Monto Total
                </Typography>
              </Grid>
              <Grid item sm>
                <Typography color="black2" fontSize="14px" lineHeight="18px" variant="body2">
                  {basicInfo.totalAmount ? <Strong>{formatTypeCurrency(currency, basicInfo.totalAmount)}</Strong> : ''}
                </Typography>
              </Grid>
            </Grid>
          </Card>
        </Grid>
      </Grid>

      <Grid container xs={12} sm={12} margin="20px 0">
        <PurchaseOrderStatusDeclaration swornDeclaration={swornDeclaration} />
      </Grid>
    </>
  )
}

export default PurchaseOrderStatusContainer
